import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Check, Crown, Sparkles, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useSession } from '@/hooks/use-auth'
import { api } from '@/lib/api'
import { useState, useEffect } from 'react'
import { toast } from '@/lib/toast'

interface BillingStatus {
  role: string
  subscription: {
    status: string
    priceId: string
    currentPeriodEnd: string | null
    cancelAtPeriodEnd: boolean
  } | null
  // One-time (5-year) entitlement — present only for a paid one-time buyer
  // with no active subscription. Access ends at this date and does not renew.
  oneTimePremiumUntil: string | null
}

type PlanKey = 'monthly' | 'yearly' | 'fiveYear'

const plans: { key: PlanKey; months: number }[] = [
  { key: 'monthly', months: 1 },
  { key: 'yearly', months: 12 },
  { key: 'fiveYear', months: 60 },
]

const FREE_FEATURES = [
  'billing.features.contacts',
  'billing.features.circles',
  'billing.features.interactions',
  'billing.features.reminders',
  'billing.features.linkedinFree',
]

const PREMIUM_FEATURES = [
  'billing.features.everything',
  'billing.features.aiInsights',
  'billing.features.aiChat',
  'billing.features.aiSummaries',
  'billing.features.unlimitedLinkedin',
  'billing.features.advancedAnalytics',
  'billing.features.unlimitedVoice',
]

export default function BillingPage() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('yearly')
  const qc = useQueryClient()

  const billingStatus = useQuery({
    queryKey: ['billing-status'],
    queryFn: () => api<BillingStatus>('/api/billing/status'),
  })

  const checkout = useMutation({
    mutationFn: (plan: PlanKey) =>
      api<{ url: string }>('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan }),
      }),
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url
    },
  })

  const portal = useMutation({
    mutationFn: () => api<{ url: string }>('/api/billing/portal', { method: 'POST' }),
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url
    },
  })

  useEffect(() => {
    if (searchParams.get('billing') === 'success') {
      setSearchParams({}, { replace: true })
      // Sync subscription from Stripe before refreshing queries
      // This handles the race where redirect arrives before the webhook
      api<{ role: string }>('/api/billing/sync', { method: 'POST' })
        .then(() => {
          qc.invalidateQueries({ queryKey: ['session'] })
          qc.invalidateQueries({ queryKey: ['billing-status'] })
          toast.success(t('billing.success'))
        })
        .catch(() => {
          // Fallback: still invalidate queries — webhook may have already handled it
          qc.invalidateQueries({ queryKey: ['session'] })
          qc.invalidateQueries({ queryKey: ['billing-status'] })
          toast.success(t('billing.success'))
        })
    }
  }, [searchParams, t, qc, setSearchParams])

  // Use billing status role (read from DB) as source of truth — session cookie cache may lag
  const isPremium = billingStatus.data?.role === 'premium' || session?.user.role === 'premium'
  const sub = billingStatus.data?.subscription
  const oneTimeUntil = billingStatus.data?.oneTimePremiumUntil ?? null

  return (
    <div className="flex flex-1 flex-col px-6 pt-6 pb-8">
      <h1 className="font-heading mb-2 text-2xl font-bold">{t('billing.title')}</h1>
      <p className="text-on-surface-variant mb-6 text-sm">{t('billing.subtitle')}</p>

      {/* Current status */}
      {isPremium && (sub || oneTimeUntil) && (
        <section className="bg-primary/10 border-primary/30 mb-6 rounded-xl border p-4">
          <div className="flex items-center gap-2">
            <Crown className="text-primary size-5" />
            <span className="font-semibold">{t('billing.activePremium')}</span>
          </div>
          {sub && sub.currentPeriodEnd && (
            <p className="text-on-surface-variant mt-1 text-sm">
              {sub.cancelAtPeriodEnd
                ? t('billing.cancelsAt', {
                    date: new Date(sub.currentPeriodEnd).toLocaleDateString(),
                  })
                : t('billing.renewsAt', {
                    date: new Date(sub.currentPeriodEnd).toLocaleDateString(),
                  })}
            </p>
          )}
          {/* One-time (5-year) buyer: show the access window; it does not renew,
              and there's no Stripe portal for a one-off payment. */}
          {!sub && oneTimeUntil && (
            <p className="text-on-surface-variant mt-1 text-sm">
              {t('billing.accessUntil', {
                date: new Date(oneTimeUntil).toLocaleDateString(),
              })}
            </p>
          )}
          {sub && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
            >
              {portal.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              {t('billing.manageSubscription')}
            </Button>
          )}
        </section>
      )}

      {/* Plan selector (only for free users) */}
      {!isPremium && (
        <>
          <div className="bg-surface-container-low mb-4 flex gap-2 rounded-xl p-1">
            {plans.map((plan) => (
              <button
                key={plan.key}
                onClick={() => setSelectedPlan(plan.key)}
                className={cn(
                  'flex-1 rounded-lg py-2 text-center text-sm font-medium transition-colors',
                  selectedPlan === plan.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-foreground',
                )}
              >
                {t(`billing.plan.${plan.key}`)}
                {plan.key === 'yearly' && (
                  <span className="ml-1 text-xs opacity-80">{t('billing.popular')}</span>
                )}
              </button>
            ))}
          </div>

          {/* Price display */}
          <div className="mb-6 text-center">
            <div className="text-4xl font-bold">
              {t(`billing.price.${selectedPlan}`)}
              {/* The 5-year plan is a one-time total, not a monthly rate. */}
              {selectedPlan !== 'fiveYear' && (
                <span className="text-on-surface-variant text-base font-normal">
                  {t('billing.perMonth')}
                </span>
              )}
            </div>
            {selectedPlan !== 'monthly' && (
              <p className="text-on-surface-variant mt-1 text-sm">
                {t(`billing.billed.${selectedPlan}`)}
              </p>
            )}
          </div>

          {/* CTA */}
          <Button
            className="mb-8 w-full rounded-xl py-6 text-base"
            onClick={() => checkout.mutate(selectedPlan)}
            disabled={checkout.isPending}
          >
            {checkout.isPending ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 size-4" />
            )}
            {t('billing.subscribe')}
          </Button>
        </>
      )}

      {/* Feature comparison */}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Free tier */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <h3 className="mb-3 font-semibold">{t('billing.freeTier')}</h3>
          <ul className="space-y-2">
            {FREE_FEATURES.map((key) => (
              <li key={key} className="flex items-start gap-2 text-sm">
                <Check className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Premium tier */}
        <section className="border-primary/30 bg-primary/5 rounded-xl border p-4">
          <h3 className="text-primary mb-3 flex items-center gap-2 font-semibold">
            <Crown className="size-4" />
            {t('billing.premiumTier')}
          </h3>
          <ul className="space-y-2">
            {PREMIUM_FEATURES.map((key) => (
              <li key={key} className="flex items-start gap-2 text-sm">
                <Check className="text-primary mt-0.5 size-4 shrink-0" />
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
